import { useQueryClient } from '@tanstack/react-query';
import { UserKeys } from '../../../../api/users-api';
import { Dispatch, SetStateAction } from 'react';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
  UserViewRoles,
} from '../../../../utils/roles/client-roles';
import { Messages } from '../../../../utils/message-constants';
import { useApplyGroupTemplatePermission } from '../../group-details/apply-template-dialog';
import { ModuleList } from '../../../../shared/analytics/module-list';
import SharedGridStateIconButton from '../../../../shared/shared-grid-state-icon-button-latest';
import SharedToolbar from '../../../../shared/shared-toolbar-latest';
import CreateButton from '../../../../shared/buttons/create-button';
import SharedIconButton from '../../../../shared/buttons/shared-icon-button';
interface GroupsManagementToolbarProps {
  directoryRoles: string[];
  userViewRoles: string[];
  setOpenAddGroup: Dispatch<SetStateAction<boolean>>;
  setOpenDeleteGroups: Dispatch<SetStateAction<boolean>>;
  setOpenApplyTemplate: Dispatch<SetStateAction<boolean>>;
  selectedRows: string[];
  userView: boolean;
}

export default function GroupManagementToolbar({
  directoryRoles,
  setOpenAddGroup,
  setOpenDeleteGroups,
  setOpenApplyTemplate,
  selectedRows,
  userView,
  userViewRoles,
}: GroupsManagementToolbarProps) {
  const queryClient = useQueryClient();
  const { canApplyTemplate, disabledReason: applyTemplateDisabledReason } =
    useApplyGroupTemplatePermission(directoryRoles ?? []);

  return (
    <SharedToolbar
      quickFilterPlaceholder={'Search by Email or Email Alias...'}
      quickFilterDebounceMs={300}
      primaryButtons={
        <CreateButton
          disabled={
            !userView
              ? !isAbleToSeeUIElement(
                  directoryRoles ?? [],
                  DirectoryRoles.CreateGroup.value
                )
              : !isAbleToSeeUIElement(
                  userViewRoles ?? [],
                  UserViewRoles.CreateGroup.value
                )
          }
          onClick={() => setOpenAddGroup(true)}
          label="Add Group"
          tooltipLabel={
            (
              !userView
                ? !isAbleToSeeUIElement(
                    directoryRoles ?? [],
                    DirectoryRoles.CreateGroup.value
                  )
                : !isAbleToSeeUIElement(
                    userViewRoles ?? [],
                    UserViewRoles.CreateGroup.value
                  )
            )
              ? Messages.Errors.Permission.Acting(
                  !userView
                    ? DirectoryRoles.CreateGroup.name
                    : UserViewRoles.CreateGroup.name
                )
              : ''
          }
        />
      }
      secondaryButtons={
        <>
          {!userView ? (
            <SharedIconButton
              variant="applyTemplate"
              onClick={() => setOpenApplyTemplate(true)}
              disabled={selectedRows.length === 0 || !canApplyTemplate}
              tooltipLabel={
                !canApplyTemplate
                  ? applyTemplateDisabledReason
                  : 'Apply Template'
              }
            />
          ) : null}
          {!userView ? (
            <SharedIconButton
              variant="delete"
              onClick={() => setOpenDeleteGroups(true)}
              disabled={
                selectedRows.length === 0 ||
                !isAbleToSeeUIElement(
                  directoryRoles,
                  DirectoryRoles.DeleteGroup.value
                )
              }
              tooltipLabel="Delete"
            />
          ) : null}
          <SharedIconButton
            variant="refresh"
            onClick={() => {
              void queryClient.invalidateQueries({
                queryKey: [UserKeys.GroupsIndexedPage],
              });
              [
                UserKeys.DirectoryGroup,
                UserKeys.GroupSettings,
                UserKeys.GroupLabels,
              ].forEach((key) => {
                void queryClient.invalidateQueries({ queryKey: [key] });
              });
            }}
            tooltipLabel="Refresh"
          />
          <SharedGridStateIconButton
            gridStateKey="groupsIndexedGridStates"
            module={ModuleList.modules.drive.name}
            section={ModuleList.modules.directory.sections.groups}
          />
        </>
      }
    />
  );
}
